import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn"; // Simplified Chinese
import "dayjs/locale/en"; // English

// Import local translation files
import enTranslations from "./locales/en.json";
import zhHansCNTranslations from "./locales/zh-Hans-CN.json";
import zhHantTWTranslations from "./locales/zh-Hant-TW.json";

import { detectUserLocale } from "./utils/multi-language";

const resources = {
  en: {
    translation: enTranslations,
  },
  "zh-Hans-CN": {
    translation: zhHansCNTranslations,
  },
  "zh-Hant-TW": {
    translation: zhHantTWTranslations,
  },
};

// Function to get language from URL search params
const getLanguageFromURL = () => {
  if (typeof window === "undefined") return null;
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get("lang");

  // Check if the language from URL is supported
  if (langParam && Object.keys(resources).includes(langParam)) {
    return langParam;
  }
  return null;
};

// Detect language and then initialize i18n
const initializeI18n = async () => {
  // First check URL params
  const urlLanguage = getLanguageFromURL();

  // If no URL language, fallback to detected locale
  const { language: detectedLanguage } = await detectUserLocale();

  const language = urlLanguage || detectedLanguage;

  i18n.use(initReactI18next).init({
    resources,
    lng: language, // Set detected language
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
    react: {
      wait: true,
      useSuspense: false,
    },
  });

  // Function to sync i18n and dayjs locales
  const updateDayjsLocale = (lng) => {
    const dayjsLocaleMap = {
      en: "en",
      "zh-Hans-CN": "zh-cn",
    };
    const dayjsLocale = dayjsLocaleMap[lng] || "en"; // Default to English
    dayjs.locale(dayjsLocale);
  };

  // Initialize i18n and sync with dayjs
  i18n.on("languageChanged", (lng) => {
    updateDayjsLocale(lng);
  });
};

// Call initialization — export the promise so App.js can gate rendering on it
const i18nReady = initializeI18n();

export default i18n;
export { i18nReady };

export async function refreshI18n() {
  // First check URL params
  const urlLanguage = getLanguageFromURL();

  // If no URL language, fallback to detected locale
  const { language: detectedLanguage } = await detectUserLocale();

  const language = urlLanguage || detectedLanguage;
  i18n.changeLanguage(language);
}
